import React from 'react';
import { stripHtml } from '../helpers';
import { ModifiedBadge } from '../displays';

// Fields to exclude from display (metadata fields)
const EXCLUDED_FIELDS = [
  'locale',
  'id',
  '__typename',
  'createdAt',
  'updatedAt',
];

function truncate(text, maxLen = 100) {
  if (!text) return '';
  return text.length > maxLen ? `${text.slice(0, maxLen)}...` : text;
}

function formatFieldName(fieldName) {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// Raw value, used for change detection: translation fields hold rich text, so
// comparing stripped text would hide any change confined to the markup.
function rawValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return String(value);
}

// Readable value, used for display only
function textValue(value) {
  return stripHtml(value) || '';
}

export default function TranslationDiff({
  beforeTranslation,
  afterTranslation,
}) {
  const locale = (
    afterTranslation?.locale ||
    beforeTranslation?.locale ||
    '??'
  ).toUpperCase();

  // Collect all unique field names from both translations
  const allFields = new Set([
    ...Object.keys(beforeTranslation || {}),
    ...Object.keys(afterTranslation || {}),
  ]);

  // Filter out excluded fields and build comparison data
  const fieldComparisons = [];
  for (const fieldName of allFields) {
    if (EXCLUDED_FIELDS.includes(fieldName)) continue;

    const beforeRaw = rawValue(beforeTranslation?.[fieldName]);
    const afterRaw = rawValue(afterTranslation?.[fieldName]);

    // Skip if both are empty
    if (!beforeRaw && !afterRaw) continue;

    const hasChanged = beforeRaw !== afterRaw;
    const beforeValue = textValue(beforeRaw);
    const afterValue = textValue(afterRaw);

    // Skip untouched fields holding no readable content
    if (!hasChanged && !afterValue) continue;

    fieldComparisons.push({
      fieldName,
      beforeValue,
      afterValue,
      hasChanged,
      // Change limited to the markup: showing the same text struck-through in
      // red then repeated in green would read as a content change
      isFormattingOnly: hasChanged && beforeValue === afterValue,
    });
  }

  // Check if there are any changes
  const hasAnyChanges = fieldComparisons.some((f) => f.hasChanged);

  return (
    <div
      className={`p-3 bg-gray-50 rounded border ${hasAnyChanges ? 'border-amber-200' : 'border-gray-200'}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded inline-flex items-center gap-1">
          <i className="fa-regular fa-language" />
          {locale}
        </span>
        {hasAnyChanges && <ModifiedBadge />}
      </div>

      <div className="space-y-3">
        {fieldComparisons.map(
          ({
            fieldName,
            beforeValue,
            afterValue,
            hasChanged,
            isFormattingOnly,
          }) => (
            <div key={fieldName} className="text-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-gray-500">
                  {formatFieldName(fieldName)}
                </span>
                {isFormattingOnly && (
                  <span className="px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                    formatting only
                  </span>
                )}
              </div>
              {hasChanged && !isFormattingOnly ? (
                <div className="space-y-1">
                  <div className="px-2 py-1 rounded text-sm bg-red-50 text-red-700 line-through max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
                    {beforeValue || (
                      <span className="text-gray-400 italic no-underline">
                        empty
                      </span>
                    )}
                  </div>
                  <div className="px-2 py-1 rounded text-sm bg-green-50 text-green-700 max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
                    {afterValue || (
                      <span className="text-gray-400 italic">empty</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="px-2 py-1 rounded text-sm bg-white text-gray-700 border border-gray-100">
                  {truncate(afterValue)}
                </div>
              )}
            </div>
          ),
        )}
      </div>
    </div>
  );
}
